import React, { useEffect, useState } from "react";
import ItemList from "../ItemList/index";
import { useParams } from "react-router-dom";
import { GetDatos, buscarFichas } from "../../json/Datos";
import { Spinner } from 'react-bootstrap';
import "./index.css";

const TIPO_OPERACION_API = {
  Ventas: 'V',
  Alquileres: 'A'
};

const precioNumerico = (ficha) => {
  const valor = Number(ficha.venta_precio) || Number(ficha.alquiler_precio) || 0;
  return valor;
};

// R: reservada, S: suspendida, V: vendida, A: alquilada.
const NO_DISPONIBLES = ['R', 'S', 'V', 'A'];

const dentroDeRango = (valor, min, max) => {
  const num = Number(valor) || 0;
  if (min && num < Number(min)) return false;
  if (max && num > Number(max)) return false;
  return true;
};

// Los códigos de parametros.ambientes ("3A", "4A2D", "0A0D"...) siempre
// empiezan con la cantidad de ambientes: alcanza para un match aproximado
// contra el catálogo cacheado (solo se usa en la búsqueda por texto).
const coincideAmbientes = (producto, codigoAmbientes) => {
  if (!codigoAmbientes || codigoAmbientes === 'cualquiera') return true;
  const cantidad = parseInt(codigoAmbientes, 10);
  if (Number.isNaN(cantidad)) return true;
  return Number(producto.ambientes_2) === cantidad;
};

const coincideMoneda = (producto, moneda) => {
  if (!moneda || moneda === 'cualquiera') return true;
  const monedaProducto = producto.venta_moneda || producto.alquiler_moneda || '';
  const simbolo = moneda === 'D' ? 'U$S' : '$';
  return monedaProducto === simbolo;
};

// Aplica el único filtro que la API no resuelve (superficie): se usa tanto
// sobre resultados ya paginados por el servidor como sobre el catálogo
// completo cuando hay búsqueda de texto. "Ocultar reservadas" va por API
// (in_esi=N en buscarFichas) y acá solo se repite para el camino de texto
// libre, que trabaja sobre el catálogo completo cacheado.
const aplicarFiltrosLocales = (productos, filtros) => {
  let resultado = productos;

  if (filtros.superficieMin || filtros.superficieMax) {
    resultado = resultado.filter((producto) =>
      dentroDeRango(producto.superficieTotal, filtros.superficieMin, filtros.superficieMax)
    );
  }

  return resultado;
};

// Filtro 100% en memoria, solo se usa cuando hay texto libre de búsqueda:
// la API no tiene un endpoint de búsqueda de texto completo, así que para
// ese caso particular se recurre al catálogo completo ya cacheado.
const filtrarPorTexto = (productos, filtros) => {
  let resultado = productos;

  if (filtros.inputValue) {
    const termino = filtros.inputValue.toLowerCase();
    resultado = resultado.filter((producto) =>
      Object.values(producto).some((valor) => String(valor).toLowerCase().includes(termino))
    );
  }

  resultado = resultado.filter((producto) => {
    const ubicacionSeleccionada = filtros.ubicacionSeleccionada || "cualquiera";
    const tipoPropiedadSeleccionada = filtros.tipoPropiedadSeleccionada || "cualquiera";
    const categoriaSeleccionada = filtros.operacionSeleccionada || "cualquiera";

    const categoriaMatch = categoriaSeleccionada === "cualquiera" ||
      (producto.operacion && producto.operacion.toLowerCase() === categoriaSeleccionada.toLowerCase());
    const ubicacionMatch = ubicacionSeleccionada.toLowerCase() === "cualquiera" ||
      (producto.ubicacion && producto.ubicacion.toLowerCase() === ubicacionSeleccionada.toLowerCase());
    const tipoPropiedadMatch = tipoPropiedadSeleccionada.toLowerCase() === "cualquiera" ||
      (producto.tipo && producto.tipo.toLowerCase() === tipoPropiedadSeleccionada.toLowerCase());

    return categoriaMatch && ubicacionMatch && tipoPropiedadMatch;
  });

  resultado = resultado.filter((producto) => coincideAmbientes(producto, filtros.ambientesSeleccionado));
  resultado = resultado.filter((producto) => coincideMoneda(producto, filtros.moneda));

  if (filtros.precioMin || filtros.precioMax) {
    resultado = resultado.filter((producto) =>
      dentroDeRango(precioNumerico(producto), filtros.precioMin, filtros.precioMax)
    );
  }

  if (filtros.ocultarReservadas) {
    resultado = resultado.filter((producto) => !NO_DISPONIBLES.includes(producto.disponibilidad));
  }

  return aplicarFiltrosLocales(resultado, filtros);
};

const RPPAGINA = 12;

// Devuelve, por ejemplo, [1, '...', 4, 5, 6, '...', 10] para marcar
// los saltos entre números de página con puntos suspensivos.
const getPaginas = (paginaActual, totalPaginas, delta = 1) => {
  const paginas = [];

  for (let i = 1; i <= totalPaginas; i++) {
    if (i === 1 || i === totalPaginas || (i >= paginaActual - delta && i <= paginaActual + delta)) {
      paginas.push(i);
    }
  }

  const conPuntos = [];
  let anterior;
  for (const pagina of paginas) {
    if (anterior !== undefined) {
      if (pagina - anterior === 2) {
        conPuntos.push(anterior + 1);
      } else if (pagina - anterior > 2) {
        conPuntos.push('...');
      }
    }
    conPuntos.push(pagina);
    anterior = pagina;
  }

  return conPuntos;
};

const ItemListContainer = ({ productosFiltrados }) => {
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [pagina, setPagina] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const { categoriaId } = useParams();

  const hayBusquedaDeTexto = Boolean(productosFiltrados?.inputValue);

  // El catálogo completo solo hace falta cuando hay texto libre de búsqueda.
  const { datos: catalogoCompleto } = GetDatos();

  useEffect(() => {
    setPagina(0);
  }, [productosFiltrados, categoriaId]);

  useEffect(() => {
    let cancelado = false;

    const cargar = async () => {
      setCargando(true);

      if (hayBusquedaDeTexto) {
        const resultado = filtrarPorTexto(catalogoCompleto || [], productosFiltrados);
        if (!cancelado) {
          setProductos(resultado);
          setTotalPaginas(1);
          setCargando(false);
        }
        return;
      }

      try {
        const operacionSeleccionada = productosFiltrados?.operacionSeleccionada;
        const ubicacionSeleccionada = productosFiltrados?.ubicacionSeleccionada;
        const tipoSeleccionado = productosFiltrados?.tipoPropiedadSeleccionada;
        const ambientesSeleccionado = productosFiltrados?.ambientesSeleccionado;
        const moneda = productosFiltrados?.moneda;

        const resultado = await buscarFichas({
          page: pagina,
          rppagina: RPPAGINA,
          tipoOperacion: TIPO_OPERACION_API[operacionSeleccionada] || '',
          ubicacion: (ubicacionSeleccionada && ubicacionSeleccionada !== 'cualquiera') ? ubicacionSeleccionada : '',
          tipoInmueble: (tipoSeleccionado && tipoSeleccionado !== 'cualquiera') ? tipoSeleccionado : '',
          ambientes: (ambientesSeleccionado && ambientesSeleccionado !== 'cualquiera') ? ambientesSeleccionado : '',
          moneda: (moneda && moneda !== 'cualquiera') ? moneda : '',
          precioMin: productosFiltrados?.precioMin || '',
          precioMax: productosFiltrados?.precioMax || '',
          soloDisponibles: Boolean(productosFiltrados?.ocultarReservadas)
        });

        if (!cancelado) {
          setProductos(aplicarFiltrosLocales(resultado.datos, productosFiltrados || {}));
          setTotalPaginas(resultado.totalPaginas || 1);
          setCargando(false);
        }
      } catch (error) {
        console.error('Error al buscar propiedades:', error);
        if (!cancelado) {
          setProductos([]);
          setTotalPaginas(1);
          setCargando(false);
        }
      }
    };

    cargar();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productosFiltrados, categoriaId, pagina, hayBusquedaDeTexto, catalogoCompleto]);

  return (
    <div>
      {cargando ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '40vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <ItemList productos={productos} />

          {!hayBusquedaDeTexto && totalPaginas > 1 && (
            <nav className="paginacion" aria-label="Paginación de resultados">
              <button
                className="pag-btn pag-btn-extremo"
                disabled={pagina === 0}
                onClick={() => setPagina(0)}
                aria-label="Primera página"
              >
                «
              </button>
              <button
                className="pag-btn"
                disabled={pagina === 0}
                onClick={() => setPagina((p) => Math.max(0, p - 1))}
                aria-label="Página anterior"
              >
                ‹
              </button>

              <div className="pag-numeros">
                {getPaginas(pagina + 1, totalPaginas).map((item, idx) =>
                  item === '...' ? (
                    <span key={`dots-${idx}`} className="pag-dots">…</span>
                  ) : (
                    <button
                      key={item}
                      className={`pag-btn pag-numero ${item === pagina + 1 ? 'activo' : ''}`}
                      onClick={() => setPagina(item - 1)}
                      aria-current={item === pagina + 1 ? 'page' : undefined}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>

              <button
                className="pag-btn"
                disabled={pagina + 1 >= totalPaginas}
                onClick={() => setPagina((p) => Math.min(totalPaginas - 1, p + 1))}
                aria-label="Página siguiente"
              >
                ›
              </button>
              <button
                className="pag-btn pag-btn-extremo"
                disabled={pagina + 1 >= totalPaginas}
                onClick={() => setPagina(totalPaginas - 1)}
                aria-label="Última página"
              >
                »
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default ItemListContainer;
